import { ApolloClient } from '@apollo/client'
import { uniqBy } from 'lodash'

import { TemplateData } from '../templates/types/blueprint'

import {
  GET_TASKS,
  GET_USER_WITH_VISITOR,
  GET_USER_WITH_WORKER,
} from './graphQL'

import {
  GetWorkerTasksUiQuery,
  GetWorkerTasksUiQueryVariables,
  RequiredTicketsUiQuery,
  TaskType,
  UserWithVisitorUiQuery,
  UserWithWorkerUiQuery,
} from 'types/api.graphql'

export type RequirePages = {
  tickets: boolean
  swms: boolean
  disabledSiteInduction: boolean
  companyInduction: boolean
  siteInduction: boolean
  signOn: boolean
}

type RequiredTicketInput = RequiredTicketsUiQuery['workerProject']
type RequiredTickets =
  RequiredTicketsUiQuery['workerProject']['requiredTickets']

export const WEB_INDUCTION_SHED_KEY = 'web-induction'

export const getUniqueTickets = (
  project: RequiredTicketInput
): RequiredTickets => {
  if (!project?.id) return []

  const projectTickets = project.requiredTickets.map(
    ({ __typename, ...rest }) => rest
  )
  const builderTickets = project.builder.requiredTickets.map(
    ({ __typename, ...rest }) => rest
  )

  return uniqBy([...projectTickets, ...builderTickets], 'id')
}

export const getInductionProgressPercent = (
  requiredFields: string[],
  formValues: Record<string, unknown>
) => {
  if (!requiredFields || !requiredFields.length) return 0

  const enteredFields = requiredFields.filter((requiredField) => {
    const value = formValues?.[requiredField]
    if (!value) return false
    if (typeof value === 'string' && !value.trim()) return false
    if (Array.isArray(value) && !value.length) return false
    return true
  })

  return (enteredFields.length / requiredFields.length) * 100
}

export const getRequiredFieldIds = (templateData: TemplateData) => {
  if (!templateData) return []

  const fields = []

  templateData?.sections?.forEach((section) => {
    section.blockGroups?.forEach((blockGroup) => {
      blockGroup.blocks?.forEach((block) => {
        if (block.kind === 'capture' && block.required) {
          fields.push(block.id)
        }
      })
    })
  })

  return fields
}

const TICKET_TASKS = [TaskType.AddTicket, TaskType.UpdateTicket]

export const getRequiredPages = async (
  client: ApolloClient<object>,
  projectId: string
): Promise<RequirePages> => {
  const tasks = await client.query<
    GetWorkerTasksUiQuery,
    GetWorkerTasksUiQueryVariables
  >({
    fetchPolicy: 'network-only',
    query: GET_TASKS,
    variables: {
      projectId,
      date: new Date().toISOString().split('T')[0],
      flags: {
        siteInductions: true,
        companyInductionNew: true,
        allowSwmsBeforeSiteInduction: true,
        swmsSignOn: true,
        ticketAdd: true,
        supportsQrCodeRotation: true,
      },
    },
  })

  const taskData = tasks?.data?.tasks

  return {
    tickets: taskData?.some((t) => TICKET_TASKS.includes(t.type)),
    swms: taskData?.some((t) => t.type === TaskType.SwmsReview),
    siteInduction: taskData?.some((t) => t.type === TaskType.ProjectInduction),
    companyInduction: taskData?.some(
      (t) => t.type === TaskType.BuilderInduction
    ),
    disabledSiteInduction: taskData?.some(
      (t) => t.type === TaskType.DisabledProjectInduction
    ),
    signOn: !taskData?.some((t) => t.type === TaskType.PendingInductionReview),
  }
}

export const getUserWithWorker = async (client: ApolloClient<object>) => {
  const userWithWorker = await client.query<UserWithWorkerUiQuery>({
    query: GET_USER_WITH_WORKER,
    fetchPolicy: 'network-only',
  })

  return userWithWorker
}

export const getUserWithVisitor = async (client: ApolloClient<object>) => {
  const userWithVisitor = await client.query<UserWithVisitorUiQuery>({
    query: GET_USER_WITH_VISITOR,
    fetchPolicy: 'network-only',
  })

  return userWithVisitor
}
